import React, { useMemo, useState } from "react";
import { Calendar, List } from "lucide-react";
import BlockRenderer from "../components/BlockRenderer";
import Lightbox from "../components/Lightbox";
import {
  CrumbLine,
  CtaPair,
  EmptyCta,
  EnquiryRail,
  HeroBand,
  SiblingPills,
  useEnquiry,
} from "./parts";

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * ARTICLE page - prose-led pages and blog posts.
 * A narrow, comfortable reading measure with an auto-built section index on
 * the left and the enquiry rail on the right.
 */
export default function ArticleTemplate({
  page,
  siblings = [],
  breadcrumbs = [],
  parentLabel,
  phone,
}) {
  const [lb, setLb] = useState(null);
  const heroImg = page.hero_image || page.thumb_image;
  const to = useEnquiry(page, heroImg, parentLabel);
  const blocks = page.blocks || [];
  const heading = page.h1 && page.h1.length < 90 ? page.h1 : page.title;

  const galleryUrls = useMemo(
    () => blocks.filter((b) => b.type === "image").map((b) => b.url),
    [blocks]
  );

  // section index from the h2/h3 headings in the migrated content
  const toc = useMemo(
    () =>
      blocks
        .filter((b) => b.type === "heading" && (b.level || 2) <= 3 && b.text)
        .map((b) => ({ id: slugify(b.text), text: b.text })),
    [blocks]
  );

  const date = (page.slug || "").match(/^(\d{4})\/(\d{2})\/(\d{2})\//);

  return (
    <div data-testid="template-article">
      <HeroBand image={heroImg} dim={0.2}>
        <CrumbLine breadcrumbs={breadcrumbs} parentLabel={parentLabel} />
        <div className="mt-2 max-w-3xl">
          <h1
            className="text-3xl font-bold leading-tight text-[color:var(--nq-sand)] md:text-5xl"
            data-testid="page-title"
          >
            {heading}
          </h1>
          {date && (
            <p className="mt-4 flex items-center gap-2 text-xs text-[color:var(--nq-stone)]">
              <Calendar className="h-3.5 w-3.5 gold-text" />
              {date[3]}/{date[2]}/{date[1]}
            </p>
          )}
          {page.meta_description && (
            <p className="mt-4 text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-lg">
              {page.meta_description}
            </p>
          )}
          <div className="mt-7">
            <CtaPair to={to} label="Enquire now" phone={phone} />
          </div>
        </div>
      </HeroBand>

      <div className="container-nq section-nq">
        {blocks.length > 0 ? (
          <div className="grid gap-10 lg:grid-cols-12">
            {toc.length > 2 && (
              <nav className="lg:col-span-2" aria-label="On this page" data-testid="article-toc">
                <div className="sticky top-28">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[color:var(--nq-stone)]">
                    <List className="h-3.5 w-3.5 gold-text" /> On this page
                  </p>
                  <ul className="mt-3 space-y-2 border-l pl-3" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                    {toc.map((t) => (
                      <li key={t.id}>
                        <a
                          href={`#${t.id}`}
                          className="block text-xs leading-snug text-[color:var(--nq-stone)] transition-colors duration-200 hover:text-[color:var(--nq-gold)]"
                          data-testid="article-toc-link"
                        >
                          {t.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </nav>
            )}

            <article
              className={toc.length > 2 ? "lg:col-span-7" : "lg:col-span-8"}
              data-testid="article-body"
            >
              <BlockRenderer
                blocks={blocks}
                onImageClick={(url) => setLb(galleryUrls.indexOf(url))}
              />
            </article>

            <aside className="lg:col-span-3">
              <EnquiryRail page={page} to={to} phone={phone} siblings={siblings} />
            </aside>
          </div>
        ) : (
          <EmptyCta page={page} to={to} siblings={siblings} />
        )}

        {blocks.length > 0 && (
          <div className="mt-14">
            <SiblingPills siblings={siblings} />
          </div>
        )}
      </div>

      <Lightbox images={galleryUrls} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
    </div>
  );
}
